/**
 * Where a customer enters a school's shop — the single rule behind the header
 * menu items, the popup intent, landing card clicks and the shop-page redirects.
 *
 * A shop may carry GROUPS ("sub-shops", `seasons[].groups[]`, each optionally
 * mapped to a header menu item like the shop itself). On DK the shop ROOT
 * (`/school-shop/{school}/{shop}`) is never a link target when the shop has
 * groups: the entry is always a group page.
 *
 *  shop  = the shop whose group is mapped to the menu item, else the shop mapped
 *          directly (`seasons[].menuItemKey`), else — `shopEntry` only — the
 *          stored shop / the school's first open shop;
 *  group = the shop's group mapped to the menu item, else its FIRST group, else
 *          null (root — the shop has no groups).
 *
 * `findMenuDestination` = mapped destinations only (drives menu-item
 * visibility); `shopEntry` adds the any-open-shop fallback for links that
 * must land somewhere. No open shop at all → nulls (callers show the closed page).
 */
export function pickGroup(shop, menuKey = null) {
  const groups = Array.isArray(shop?.groups) ? shop.groups.filter((g) => g?.slug) : [];
  return (menuKey ? groups.find((g) => g.menuItemKey === menuKey) : null) ?? groups[0] ?? null;
}

export function findMenuDestination(school, menuKey) {
  const shops = Array.isArray(school?.seasons) ? school.seasons.filter((s) => s?.slug) : [];
  if (!menuKey) return { shop: null, group: null };
  const viaGroup = shops.find((sh) => (sh.groups || []).some((g) => g?.menuItemKey === menuKey));
  if (viaGroup) return { shop: viaGroup, group: pickGroup(viaGroup, menuKey) };
  const direct = shops.find((sh) => sh.menuItemKey === menuKey);
  if (direct) return { shop: direct, group: pickGroup(direct, menuKey) };
  return { shop: null, group: null };
}

export function shopEntry(school, menuKey = null, preferredShop = null) {
  const shops = Array.isArray(school?.seasons) ? school.seasons.filter((s) => s?.slug) : [];
  const mapped = findMenuDestination(school, menuKey);
  if (mapped.shop) return mapped;
  const same = (a, b) => Boolean(a && b && ((a.id != null && b.id != null && String(a.id) === String(b.id)) || (a.slug && a.slug === b.slug)));
  const shop = shops.find((sh) => same(sh, preferredShop)) ?? shops[0] ?? null;
  return shop ? { shop, group: pickGroup(shop, menuKey) } : { shop: null, group: null };
}

/** `/clothing/{fe}/school-shop/{school}/{shop}[/{group}]` */
export function schoolShopPath(feSlug, schoolSlug, shop, group = null) {
  const base = `/clothing/${feSlug}/school-shop/${encodeURIComponent(schoolSlug)}/${encodeURIComponent(shop.slug)}`;
  return group?.slug ? `${base}/${encodeURIComponent(group.slug)}` : base;
}
